/**
 * GravBase Chat Widget: Parker
 * Lazy-loaded, localStorage session, SSE streaming
 */

(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────
  const WORKER_URL  = 'https://anj-chat-worker.dry-band-bd68.workers.dev/chat';
  const STORAGE_KEY = 'gravbase_chat_session';
  const MAX_HISTORY = 20;
  const BOT_NAME    = 'Parker';
  const LEAD_SENT_KEY = 'anj_lead_sent';
  // A random id for this chat, sent with each message so Parker's chat log can group a
  // chat's messages (2026-09-30). It holds nothing about the visitor, and Clear starts
  // a new one.
  const CONVERSATION_KEY = 'gravbase_chat_conversation';
  const GREETING    = "Hi, I'm Parker. I can help you find the right vehicle wrap template, explain the free and Design-Ready PDF versions, and point you to GravBase's free design tools. What vehicle are you working on?";
  const LOGO_URL    = (typeof window.ANJ_CHAT_LOGO !== 'undefined') ? window.ANJ_CHAT_LOGO : '';

  // ── State ─────────────────────────────────────────────────
  let isOpen        = false;
  let isStreaming   = false;
  let greetingShown = false;
  let messages      = [];
  let activeAbort   = null;
  let conversation  = '';

  // ── DOM refs ──────────────────────────────────────────────
  let launcher, window_, msgList, input, sendBtn, badge, charCounter;

  // ── Boot ─────────────────────────────────────────────────
  function init() {
    injectHTML();
    bindRefs();
    bindEvents();
    loadSession();
    renderHistory();
    if (messages.length === 0) {
      greetingShown = false;
    } else {
      greetingShown = true;
      // Re-open automatically if they had an active session
      setTimeout(openChat, 600);
    }
  }

  // ── HTML scaffold ─────────────────────────────────────────
  function injectHTML() {
    const el = document.createElement('div');
    el.id = 'anj-chat-root';
    el.innerHTML = `
      <button id="anj-chat-launcher" aria-label="Chat with Parker at GravBase" aria-expanded="false">
        <svg class="anj-icon-chat" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm0 14H6l-2 2V4h16v12z"/>
        </svg>
        <svg class="anj-icon-close" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
        <span id="anj-chat-badge" aria-hidden="true"></span>
      </button>

      <div id="anj-chat-window" role="dialog" aria-label="Chat with Parker" aria-hidden="true">
        <div id="anj-chat-header">
          <div class="anj-parker-avatar" aria-hidden="true">${avatarInnerHTML()}</div>
          <div class="anj-header-info">
            <div class="anj-header-name">${BOT_NAME}</div>
            <div class="anj-header-status">
              <span class="anj-status-dot"></span>
              GravBase · Online
            </div>
          </div>
          <button class="anj-header-btn" id="anj-chat-minimize" aria-label="Minimize chat" title="Minimize">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19 13H5v-2h14v2z"/>
            </svg>
          </button>
          <button class="anj-header-btn" id="anj-chat-clear" aria-label="Clear conversation" title="Clear conversation">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
          </button>
        </div>

        <div id="anj-chat-messages" aria-live="polite" aria-label="Chat messages"></div>

        <div id="anj-chat-input-area">
          <textarea
            id="anj-chat-input"
            rows="1"
            placeholder="Ask about a vehicle, a template or a format…"
            aria-label="Type your message"
            maxlength="1000"
          ></textarea>
          <button id="anj-chat-send" aria-label="Send message" disabled>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
        <div id="anj-char-counter" aria-live="polite"></div>
        <!-- Notice approved by the owner, 2026-09-30, when Parker's chat log was switched on -->
        <p id="anj-chat-notice">AI chat sessions may or may not be saved over time.</p>
      </div>
    `;
    document.body.appendChild(el);
  }

  function bindRefs() {
    launcher    = document.getElementById('anj-chat-launcher');
    window_     = document.getElementById('anj-chat-window');
    msgList     = document.getElementById('anj-chat-messages');
    input       = document.getElementById('anj-chat-input');
    sendBtn     = document.getElementById('anj-chat-send');
    badge       = document.getElementById('anj-chat-badge');
    charCounter = document.getElementById('anj-char-counter');
  }

  // ── Events ────────────────────────────────────────────────
  function bindEvents() {
    launcher.addEventListener('click', toggleChat);
    document.getElementById('anj-chat-minimize').addEventListener('click', closeChat);
    document.getElementById('anj-chat-clear').addEventListener('click', clearChat);
    sendBtn.addEventListener('click', handleSend);

    input.addEventListener('input', function () {
      this.style.height = 'auto';
      this.style.height = Math.min(this.scrollHeight, 100) + 'px';
      sendBtn.disabled = !this.value.trim() || isStreaming;
      const len = this.value.length;
      if (len > 800) {
        charCounter.textContent = (1000 - len) + ' left';
        charCounter.classList.add('visible');
      } else {
        charCounter.classList.remove('visible');
      }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (!sendBtn.disabled) handleSend();
      }
    });

    document.addEventListener('click', function (e) {
      if (isOpen && !window_.contains(e.target) && !launcher.contains(e.target)) {
        closeChat();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) closeChat();
    });

    window.addEventListener('pagehide', function () {
      if (activeAbort) activeAbort.abort();
    });
  }

  // ── Open / close ──────────────────────────────────────────
  function toggleChat() {
    isOpen ? closeChat() : openChat();
  }

  function openChat() {
    isOpen = true;
    launcher.classList.add('is-open');
    launcher.setAttribute('aria-expanded', 'true');
    window_.classList.add('is-open');
    window_.setAttribute('aria-hidden', 'false');
    badge.classList.remove('visible');

    // Show greeting the first time the chat is opened
    if (!greetingShown) {
      greetingShown = true;
      setTimeout(() => appendBotMessage(GREETING), 300);
    }

    scrollToBottom();
    setTimeout(() => input.focus(), 250);
  }

  function closeChat() {
    isOpen = false;
    launcher.classList.remove('is-open');
    launcher.setAttribute('aria-expanded', 'false');
    window_.classList.remove('is-open');
    window_.setAttribute('aria-hidden', 'true');
  }

  // ── Session ───────────────────────────────────────────────
  function loadSession() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) messages = JSON.parse(raw);
    } catch {
      messages = [];
    }
  }

  function saveSession() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_HISTORY)));
    } catch {}
  }

  // The chat's id: kept with the session, made when there is none. A browser that
  // blocks storage still gets one id for as long as the page is open.
  function conversationId() {
    if (conversation) return conversation;
    try {
      conversation = localStorage.getItem(CONVERSATION_KEY) || '';
    } catch {}
    if (!/^[A-Za-z0-9_-]{8,64}$/.test(conversation)) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      conversation = 'c_' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      try {
        localStorage.setItem(CONVERSATION_KEY, conversation);
      } catch {}
    }
    return conversation;
  }

  function clearChat() {
    messages = [];
    greetingShown = false;
    localStorage.removeItem(STORAGE_KEY);
    conversation = '';
    try {
      localStorage.removeItem(CONVERSATION_KEY);
    } catch {}
    msgList.innerHTML = '';
  }

  // ── Render helpers ────────────────────────────────────────
  function renderHistory() {
    messages.forEach(m => {
      if (m.role === 'user') appendUserMessage(m.content, false);
      else appendBotMessage(m.content, false);
    });
    scrollToBottom();
  }

  /* Avatar inner content: the GravBase mark when a logo URL is configured
     (set via window.ANJ_CHAT_LOGO in anj-chat-widget.liquid), else the
     letter "P". The wrapper is aria-hidden, so the img alt stays empty. */
  function avatarInnerHTML() {
    return LOGO_URL ? `<img src="${LOGO_URL}" alt="">` : 'P';
  }

  function parkerAvatarHTML() {
    return `<div class="anj-msg-avatar" aria-hidden="true">${avatarInnerHTML()}</div>`;
  }

  function appendUserMessage(text, animate = true) {
    const row = document.createElement('div');
    row.className = 'anj-msg anj-msg--user';
    if (!animate) row.style.animation = 'none';
    row.innerHTML = `<div class="anj-msg-bubble">${escapeHtml(text)}</div>`;
    msgList.appendChild(row);
    scrollToBottom();
    return row;
  }

  function appendBotMessage(text, animate = true) {
    const row = document.createElement('div');
    row.className = 'anj-msg anj-msg--bot';
    if (!animate) row.style.animation = 'none';
    row.innerHTML = `${parkerAvatarHTML()}<div class="anj-msg-bubble">${formatMessage(text)}</div>`;
    msgList.appendChild(row);
    scrollToBottom();
    return row;
  }

  function showTyping() {
    const row = document.createElement('div');
    row.className = 'anj-msg anj-msg--bot';
    row.id = 'anj-typing-row';
    row.innerHTML = `${parkerAvatarHTML()}<div class="anj-msg-bubble"><div class="anj-typing"><span></span><span></span><span></span></div></div>`;
    msgList.appendChild(row);
    scrollToBottom();
  }

  function removeTyping() {
    const el = document.getElementById('anj-typing-row');
    if (el) el.remove();
  }

  function showLeadNotice() {
    const notice = document.createElement('div');
    notice.className = 'anj-lead-notice';
    notice.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
      <span>Your info has been sent to our team. Expect a follow-up within 1 business day.</span>
    `;
    msgList.appendChild(notice);
    scrollToBottom();
  }

  function scrollToBottom() {
    msgList.scrollTop = msgList.scrollHeight;
  }

  // ── Send & stream ─────────────────────────────────────────
  async function handleSend() {
    const text = input.value.trim();
    if (!text || isStreaming) return;

    input.value = '';
    input.style.height = 'auto';
    sendBtn.disabled = true;
    isStreaming = true;

    messages.push({ role: 'user', content: text });
    saveSession();
    appendUserMessage(text);
    showTyping();

    let botText = '';
    let streamBubble = null;

    activeAbort = new AbortController();

    try {
      const res = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The page is its path only; a query can carry what a visitor typed elsewhere.
        body: JSON.stringify({ messages, conversation: conversationId(), page: location.pathname }),
        signal: activeAbort.signal,
      });

      if (res.status === 429) {
        removeTyping();
        appendBotMessage("Too many messages. Give it a moment and try again.");
        return;
      }
      if (!res.ok) throw new Error('HTTP ' + res.status);

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          if (event.type === 'chunk') {
            if (!streamBubble) {
              removeTyping();
              const row = document.createElement('div');
              row.className = 'anj-msg anj-msg--bot';
              row.innerHTML = `${parkerAvatarHTML()}<div class="anj-msg-bubble"></div>`;
              msgList.appendChild(row);
              streamBubble = row.querySelector('.anj-msg-bubble');
            }
            botText += event.text;
            // Strip from LEAD_CAPTURE onwards — indexOf is more reliable than regex across chunks
            const lcIdx = botText.indexOf('LEAD_CAPTURE:');
            const renderText = lcIdx !== -1 ? botText.slice(0, lcIdx).trimEnd() : botText;
            streamBubble.innerHTML = formatMessage(renderText);
            scrollToBottom();
          }

          if (event.type === 'lead_captured') {
            // Deduplicate — only send once per session
            const alreadySent = sessionStorage.getItem(LEAD_SENT_KEY);
            // The Worker emails and forwards the lead itself when it sees
            // LEAD_CAPTURE; the browser used to post it a second time with a
            // token that shipped in this public file (security review,
            // 2026-09-24). Now the page only shows the notice.
            if (!alreadySent) {
              sessionStorage.setItem(LEAD_SENT_KEY, '1');
              showLeadNotice();
            }
          }

          if (event.type === 'done') {
            messages.push({ role: 'assistant', content: botText });
            saveSession();
            if (!isOpen) badge.classList.add('visible');
          }
        }
      }
    } catch (err) {
      removeTyping();
      appendBotMessage(`Something went wrong on our end. Try again or reach us through our [contact page](${window.location.origin}/pages/contact).`);
      console.error('[GravBase Chat]', err);
    } finally {
      isStreaming = false;
      activeAbort = null;
      sendBtn.disabled = !input.value.trim();
    }
  }

  // ── Formatting ────────────────────────────────────────────
  function escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMessage(text) {
    text = text.replace(/\s*LEAD_CAPTURE:.*$/s, '').trim();
    let html = escapeHtml(text);
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Markdown links
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    // Full https:// URLs not already in an href
    html = html.replace(/(?<![="])https?:\/\/[^\s<]+/g, url => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
    // Bare anjgraphics.com/... paths (no protocol)
    html = html.replace(/(?<![="\/])(anjgraphics\.com\/[^\s<,."]*)/g, match => `<a href="https://${match}" target="_blank" rel="noopener noreferrer">${match}</a>`);
    const paras = html.split(/\n\n+/);
    html = paras.length > 1
      ? paras.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
      : html.replace(/\n/g, '<br>');
    return html;
  }

  // ── Kick off ──────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
